import {
  createInProcessEstimatorClient,
  type EstimatorClient
} from "@reno-estimator/ui";
import {
  buildSamplePricingCatalogV2,
  buildSampleRegionalPricingTable
} from "@reno-estimator/pricing-data";
import type { PricingCatalog, RegionalPricingTable } from "@reno-estimator/core";

/**
 * The one place this app wires up a pricing catalog + regional table and
 * hands them to the shared in-process EstimatorClient. No calculation
 * logic lives here - this only supplies data to the existing engine seam
 * (@reno-estimator/ui's createInProcessEstimatorClient, which itself just
 * calls @reno-estimator/core's ReferenceCalculationEngine).
 *
 * Two data sources are supported, chosen automatically by environment:
 *
 *  - Standalone (dev server, tests, any non-WordPress host): the bundled
 *    sample catalog, built in-process via buildSamplePricingCatalogV2().
 *    This is the original Phase 3 behavior and needs no network access.
 *
 *  - WordPress (Phase 18): when `window.RenoEstimatorConfig.restUrl` is
 *    present (set by the plugin via wp_localize_script - see
 *    packages/wordpress/php), the catalog is instead fetched once from the
 *    plugin's own read-only REST endpoint, which currently serves the same
 *    sample data out of a WordPress option (see
 *    packages/wordpress/php/includes/class-rest.php) rather than this
 *    package's bundled copy. That's the seam a later admin-pricing-editor
 *    phase will write into, without this app changing at all - it only
 *    ever depends on the fetched JSON shape, not on where WordPress got it.
 *    If the fetch fails for any reason (route not registered yet, network
 *    error, unexpected shape), this falls back to the bundled sample
 *    catalog so the widget still produces an estimate rather than hard-
 *    failing on a WordPress-specific dependency.
 *
 * Either way, the calculation itself always runs in-process in the
 * browser - only the pricing *data* source differs. Nothing else in this
 * app needs to know which one is active, since both paths return the same
 * EstimatorClient interface.
 */

/**
 * The single host-config surface this app reads from, set by whatever
 * embeds it (the WordPress plugin via `wp_localize_script`, or a
 * standalone page's own inline script). Declared once here and reused
 * (not redeclared) by every other module that reads part of it
 * (analyticsClient.ts's GA4 measurement id, PrivacyPolicyLink.tsx's
 * policy URL) - TypeScript's global augmentation merges fine across
 * files for the `Window` interface itself, but only when each file
 * points at the same underlying type rather than declaring its own
 * differently-shaped one.
 */
export interface RenoEstimatorConfig {
  /** Trailing-slash REST base, e.g. "https://example.com/wp-json/reno-estimator/v1/". */
  restUrl?: string;
  /** GA4 measurement id (e.g. "G-XXXXXXX"). Analytics is only ever sent to GA4 when this is set - see analyticsClient.ts. */
  ga4MeasurementId?: string;
  /** A real, administrator-published privacy policy URL. See PrivacyPolicyLink.tsx for what happens when this isn't set. */
  privacyPolicyUrl?: string;
  /**
   * Phase 23: a `wp_create_nonce('wp_rest')` value, localized alongside
   * `restUrl` (see class-assets.php), sent as the `X-WP-Nonce` header on
   * the write routes this plugin now has (`/leads`, `/estimate-email` -
   * see leadStore.ts's HttpLeadStore and emailEstimateClient.ts's
   * HttpEmailTransport). The existing `/pricing` route is read-only and
   * still needs none. Not present outside WordPress.
   */
  wpRestNonce?: string;
}

declare global {
  interface Window {
    RenoEstimatorConfig?: RenoEstimatorConfig;
  }
}

interface FetchedPricingResponse {
  pricingCatalog: PricingCatalog;
  regionalPricing: RegionalPricingTable;
}

function looksLikeFetchedPricingResponse(value: unknown): value is FetchedPricingResponse {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.pricingCatalog === "object" && v.pricingCatalog !== null && typeof v.regionalPricing === "object" && v.regionalPricing !== null;
}

async function fetchWordPressPricing(restUrl: string): Promise<FetchedPricingResponse | null> {
  try {
    const response = await fetch(`${restUrl.replace(/\/$/, "")}/pricing`, {
      headers: { Accept: "application/json" },
      credentials: "omit"
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    return looksLikeFetchedPricingResponse(data) ? data : null;
  } catch {
    return null;
  }
}

let cachedClient: EstimatorClient | null = null;

export function getEstimatorClient(): EstimatorClient {
  if (cachedClient) return cachedClient;

  const restUrl = typeof window !== "undefined" ? window.RenoEstimatorConfig?.restUrl : undefined;

  if (!restUrl) {
    cachedClient = createInProcessEstimatorClient({
      pricingCatalog: buildSamplePricingCatalogV2(),
      regionalPricing: buildSampleRegionalPricingTable()
    });
    return cachedClient;
  }

  // WordPress context: defer building the real in-process client until the
  // first submit() call, since fetching the catalog is async and this
  // factory function must stay synchronous (every existing caller expects
  // an EstimatorClient back immediately, not a Promise of one).
  let deferredClient: Promise<EstimatorClient> | null = null;
  cachedClient = {
    async submit(input) {
      if (!deferredClient) {
        deferredClient = fetchWordPressPricing(restUrl).then((fetched) =>
          createInProcessEstimatorClient(
            fetched ?? {
              pricingCatalog: buildSamplePricingCatalogV2(),
              regionalPricing: buildSampleRegionalPricingTable()
            }
          )
        );
      }
      const real = await deferredClient;
      return real.submit(input);
    }
  };
  return cachedClient;
}
